import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeftIcon, ArrowPathIcon, CheckCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { classNames } from "src/helpers/classNames";
import { acquireModalLayer, releaseModalLayer, getCurrentMaxLayer } from "src/helpers/modalLayerManager";
import useOnClickOutside from "src/hooks/useOnClickOutside";

const Modal = ({
  zIndex = "",
  title,
  secondaryTitle = "",
  isOpen = false,
  size = "md",
  exitTitle = "Exit Full-Screen Preview",
  onCancel = () => {},
  onSuccess = () => {},
  onClose = null,
  defaultOptions = null,
  isLoading = false,
  defaultStyles = null,
  children,
  hideCrossIcon = false,
  closeModalOnClose = true,
  fullScreen = false,
  defaultHeader = true,
  customHeader = null,
  ...props
}) => {
  const modalSize = {
    xs: "sm:max-w-lg",
    sm: "sm:max-w-xl",
    md: "sm:max-w-2xl",
    lg: "sm:max-w-3xl",
    xl: "sm:max-w-4xl",
    xxl: "sm:max-w-5xl",
    xxxl: "sm:max-w-6xl",
    xxxxl: "sm:max-w-7xl",
    xxxxxl: "sm:max-w-[1800px]",
    full: "max-w-full",
  };

  const [options, setOptions] = useState({
    onCancelButtonVisible: true,
    onCancelButtonText: "Cancel",
    onCancelButtonDisabled: false,
    onSuccessButtonVisible: true,
    onSuccessButtonText: "Submit",
    onSuccessLoaderVisible: false,
    onSuccessLoaderStart: false,
    onSuccessButtonDisabled: false,
    onSuccessButtonSuccess: false,
  });

  const [styles, setStyles] = useState({
    containerStyles: "w-full",
    overFlowYVisible: true,
    childClasses: "",
  });

  const [lastClicked, setLastClicked] = useState("success");
  const [mounted, setMounted] = useState(isOpen);
  const [visible, setVisible] = useState(isOpen);
  const [modalLayer, setModalLayer] = useState(0);

  const panelRef = useRef(null);
  const backdropRef = useRef(null);

  useEffect(() => {
    if (defaultOptions) setOptions((prev) => ({ ...prev, ...defaultOptions }));
    if (defaultStyles) setStyles((prev) => ({ ...prev, ...defaultStyles }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOptions, defaultStyles]);

  useEffect(() => {
    let timeout;
    if (isLoading) {
      setOptions((prev) => ({ ...prev, onSuccessLoaderStart: true, onSuccessLoaderVisible: true }));
    } else {
      timeout = setTimeout(() => {
        setOptions((prev) => ({ ...prev, onSuccessLoaderStart: false, onSuccessLoaderVisible: false }));
      }, 300);
    }
    return () => clearTimeout(timeout);
  }, [isLoading]);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
      const duration = 300;
      const t = setTimeout(() => setMounted(false), duration);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const newLayer = acquireModalLayer();
      setModalLayer(newLayer);
    }
    return () => {
      if (isOpen) releaseModalLayer();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!mounted) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !isLoading) {
        if (modalLayer === getCurrentMaxLayer()) onCancel();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mounted, isLoading, onCancel, modalLayer]);

  useOnClickOutside(panelRef, () => {
    if (!isLoading) {
      if (modalLayer === getCurrentMaxLayer()) {
        if (onClose && closeModalOnClose) onClose();
        else onCancel();
      }
    }
  });

  if (!mounted) return null;

  const body = (
    <div
      className={classNames("fixed inset-0 z-[60]", fullScreen ? "" : "overflow-y-auto")}
      style={{ zIndex: zIndex ? zIndex : (fullScreen ? 40 : 60) + modalLayer }}
      role="dialog"
      aria-modal="true"
      {...props}>
      <div className={classNames(fullScreen ? "" : "overflow-y-auto", "fixed inset-0")}>
        <div
          ref={backdropRef}
          onClick={() => {
            if (!isLoading) {
              if (onClose && closeModalOnClose) onClose();
              else onCancel();
            }
          }}
          className={classNames("fixed inset-0 bg-gray-500/40 transition-opacity duration-300", visible ? "opacity-100" : "opacity-0")}
        />

        <div className={classNames("fixed inset-0 z-40 flex h-full max-h-full items-center justify-center text-center", fullScreen ? "" : "p-2 sm:px-6")}>
          <div
            ref={panelRef}
            className={classNames(
              "relative bg-white p-0 text-left shadow-xl transition-all duration-300",
              fullScreen ? "h-screen w-full" : "h-auto max-h-screen rounded-lg sm:my-8",
              modalSize[size],
              styles.containerStyles,
              visible ? "translate-y-0 opacity-100 sm:scale-100" : "translate-y-4 opacity-0 sm:translate-y-0 sm:scale-95",
            )}>
            {defaultHeader ? (
              <div className="flex items-center justify-between px-4 py-4">
                <h3 className="max-w-md truncate text-left text-xl font-semibold leading-6 text-gray-900">
                  {title} {secondaryTitle ? <span className="text-gray-400">/ {secondaryTitle}</span> : null}
                </h3>
                {!hideCrossIcon && (
                  <button
                    type="button"
                    disable={isLoading}
                    style={{ ...options?.customStyles }}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-200 focus:outline-none"
                    onClick={() => {
                      if (!isLoading) {
                        onClose ? onClose() : onCancel();
                      }
                    }}>
                    <span className="sr-only">Close</span>
                    <XMarkIcon
                      className="h-6 w-6"
                      aria-hidden="true"
                    />
                  </button>
                )}
              </div>
            ) : (
              customHeader
            )}

            {fullScreen && exitTitle && (
              <button
                onClick={onCancel}
                className="fixed left-0 top-40 z-50 flex items-center gap-x-6 rounded-r-sm bg-red-500/70 px-7 py-2 text-[28px] text-white shadow transition-all hover:bg-red-500">
                <ArrowLeftIcon className="h-6 w-6 stroke-[3px]" />
                {exitTitle}
              </button>
            )}

            <div className={classNames(styles?.overFlowYVisible ? "" : "scrollbar overflow-y-scroll", fullScreen ? "h-[calc(100vh-3rem)]" : "relative z-[1] max-h-[calc(100vh-11rem)] px-4 sm:py-2", styles?.childClasses)}>{children}</div>

            {!fullScreen && (options.onCancelButtonVisible || options.onSuccessButtonVisible) && (
              <div className="flex justify-end space-x-4 p-4">
                {options.onCancelButtonVisible && (
                  <button
                    disabled={isLoading || (options.onCancelLoaderVisible && options.onCancelLoaderStart) || (options.onSuccessLoaderVisible && options.onSuccessLoaderStart)}
                    type="button"
                    className="inline-flex h-[47px] w-full items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm md:h-[40px] 2xl:h-[47px]"
                    onClick={() => {
                      if (!isLoading) {
                        setLastClicked("cancel");
                        onCancel();
                      }
                    }}>
                    {options.onCancelButtonText}
                    {options.onCancelLoaderVisible && options.onCancelLoaderStart && lastClicked !== "success" && <ArrowPathIcon className="ml-2 h-5 w-5 animate-spin" />}
                  </button>
                )}
                {options.onSuccessButtonVisible && (
                  <button
                    type="button"
                    disabled={(options.onCancelLoaderVisible && options.onCancelLoaderStart) || (options.onSuccessLoaderVisible && options.onSuccessLoaderStart) || options.successButtonDisabled}
                    className={`inline-flex h-[47px] w-full items-center justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none sm:w-auto sm:text-sm md:h-[40px] 2xl:h-[47px] ${
                      !((options.onCancelLoaderVisible && options.onCancelLoaderStart) || (options.onSuccessLoaderVisible && options.onSuccessLoaderStart) || options.successButtonDisabled) ? "!bg-highlightColor" : "bg-slate-50 !text-slate-500"
                    }`}
                    onClick={() => {
                      setLastClicked("success");
                      onSuccess();
                    }}>
                    {options.onSuccessButtonText}
                    {options.onSuccessLoaderVisible && options.onSuccessLoaderStart && (lastClicked === "success" || !options.onCancelLoaderVisible) && (
                      <ArrowPathIcon className={`ml-2 h-5 w-5 animate-spin ${!((options.onCancelLoaderVisible && options.onCancelLoaderStart) || (options.onSuccessLoaderVisible && options.onSuccessLoaderStart)) ? "" : "text-slate-500"}`} />
                    )}
                    {options.onSuccessButtonSuccess && !options.onSuccessLoaderVisible && !options.onSuccessLoaderStart && <CheckCircleIcon className="ml-2 h-5 w-5 " />}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(body, document.body);
};

export default Modal;
